// components/organisms/DisclaimersGroup.vue
<template>
  <section class="space-y-4">
    <div class="space-y-2">
      <FormLabel label="Custom disclaimers lists"/>

      <div class="flex items-center gap-3">
        <CustomDropdown
            class="w-full"
            ref="disclaimersDropdown"
            :model-value="selectedTemplate"
            placeholder="Select a disclaimer template"
            clearable
            @clear="clearTemplate"
        >
          <ul class="space-y-1">
            <li
                v-for="group in disclaimerList"
                :key="group.name"
                class="px-4 py-2 rounded-lg hover:bg-base-200 cursor-pointer transition-colors"
                @click="selectTemplate(group.name)"
            >
              <p class="font-medium text-base text-base-content">{{ group.name }}</p>
              <p class="text-xs text-base-content/60">{{ group.count }} Disclaimers</p>
            </li>
          </ul>
        </CustomDropdown>

        <AButton class="btn-soft" @click="openModal">
          Add disclaimers
        </AButton>
      </div>
    </div>

    <div class="border border-dashed border-light/50 rounded-xl p-4">
      <template v-if="modelValue.length === 0">
        <AddProductButton actionText="Select a disclaimer" @clicked="openGeneralPoolModal"/>
      </template>
      <template v-else>
        <div class="space-y-3">
          <div class="flex items-center justify-between">
          <h4 class="font-semibold text-base text-base-content">Selected Disclaimers</h4>
            <button
                type="button"
                class="btn btn-xs btn-error btn-outline"
                @click="removeAllDisclaimers"
            >
              Remove all
            </button>
          </div>
          <ul class="space-y-2">
            <li
                v-for="(text, index) in modelValue"
                :key="index"
                class="text-sm text-base-content flex justify-between items-center gap-4"
            >
              <span>{{ text }}</span>
              <button
                  type="button"
                  class="btn btn-xs btn-error btn-outline"
                  @click="removeDisclaimer(text)"
              >
                ✕
              </button>
            </li>
          </ul>
        </div>
      </template>
    </div>

    <NewDisclaimerModal
        ref="disclaimerModal"
        :disclaimers="availableModalDisclaimers"
        @confirmed="handleSelectedDisclaimers"
        @add-new="handleAddNewDisclaimer"
    />

    <NewDisclaimerModal
        ref="generalPoolModal"
        :disclaimers="fullDisclaimerPool"
        @confirmed="handleSelectedDisclaimers"
    />
  </section>
</template>

<script setup>
import {computed, onBeforeUnmount, onMounted, ref, watch} from 'vue'
import CustomDropdown from '@/components/molecules/CustomDropdown.vue'
import AButton from '@/components/atoms/AButton.vue'
import FormLabel from '@/components/atoms/FormLabel.vue'
import NewDisclaimerModal from '@/components/molecules/AddDisclaimerModal.vue'
import AddProductButton from "@/components/molecules/AddProductButton.vue";
import {useDisclaimerStore} from '@/stores/disclaimerStore.js'

const disclaimerStore = useDisclaimerStore()

const modelValue = defineModel({type: Array, required: false, default: () => []})
const selectedTemplate = ref('')
const disclaimerModal = ref(null)
const generalPoolModal = ref(null)
const disclaimersDropdown = ref(null)
const selectedFromTemplate = ref([])

// Templates from API: { id, name, disclaimers: [{id: 'text'}] }
const disclaimerList = computed(() =>
  disclaimerStore.templates.map(t => ({
    id: t.id,
    name: t.name,
    count: t.disclaimers?.length ?? 0,
    items: (t.disclaimers ?? []).map(d => d.id),
  }))
)

const customDisclaimerList = ref([])

const selectedTemplateGroup = computed(() =>
    disclaimerList.value.find(g => g.name === selectedTemplate.value)
)

const unifiedDisclaimers = computed(() => {
  return [...selectedFromTemplate.value, ...customDisclaimerList.value]
})

// Individual disclaimers from API: disclaimer.id IS the text
const availableModalDisclaimers = computed(() =>
  disclaimerStore.disclaimers.map(d => d.id)
)

const fullDisclaimerPool = computed(() => {
  const pool = new Set()
  disclaimerList.value.forEach(group => group.items.forEach(i => pool.add(i)))
  availableModalDisclaimers.value.forEach(i => pool.add(i))
  return [...pool]
})

function selectTemplate(name) {
  selectedTemplate.value = name
  selectedFromTemplate.value = selectedTemplateGroup.value?.items || []
  disclaimersDropdown.value?.close()
}

function clearTemplate() {
  selectedTemplate.value = ''
}

function openModal() {
  disclaimerModal.value?.openModal()
}

function openGeneralPoolModal() {
  generalPoolModal.value?.openModal()
}

function handleSelectedDisclaimers(selected) {
  if (!selected || selected.length === 0) return
  customDisclaimerList.value.push(...selected.filter(i => !customDisclaimerList.value.includes(i)))
}

function handleAddNewDisclaimer() {
  // Lógica opcional si se permite crear desde modal
}

function removeDisclaimer(text) {
  const modelIdx = modelValue.value.indexOf(text)
  if (modelIdx !== -1) {
    modelValue.value.splice(modelIdx, 1)
  }
  const customIdx = customDisclaimerList.value.indexOf(text)
  if (customIdx !== -1) {
    customDisclaimerList.value.splice(customIdx, 1)
  }
  // const template = selectedTemplateGroup.value
  // if (template) {
  //   const templateIdx = template.items.indexOf(text)
  //   if (templateIdx !== -1) {
  //     template.items.splice(templateIdx, 1)
  //     template.count = template.items.length
  //   }
  // }
}

const removeAllDisclaimers = () => {
  selectedTemplate.value = ''
  selectedFromTemplate.value = []
  modelValue.value = []
  customDisclaimerList.value=[]
}

const updateCustomDisclaimers = (disclaimers) => {
  disclaimers = disclaimers.filter(d => {
    return !selectedTemplateGroup.value?.items.includes(d)
  })
  if (disclaimers && disclaimers.length > 0) {
    customDisclaimerList.value = [...disclaimers]
  }
}

onBeforeUnmount(() => {
  removeAllDisclaimers()
})

onMounted(async () => {
  await Promise.all([
    disclaimerStore.fetchTemplates(),
    disclaimerStore.fetchDisclaimers(),
  ])
  updateCustomDisclaimers(modelValue.value)
})

watch(unifiedDisclaimers, (newVal) => {
  console.log('UnifiedDisclaimers changed:', newVal)
  const missing = newVal.filter(item => !modelValue.value.includes(item))
  if (missing.length === 0) return
  modelValue.value = [...modelValue.value, ...missing]
}, {immediate: true, deep: true})

watch(modelValue, (newVal) => {
  console.log('ModelValue changed:', newVal)
  updateCustomDisclaimers(newVal)
}, {immediate: true, deep: true})
</script>